import React, { useEffect, useRef } from 'react';
import Chart from 'chart.js/auto';
import { cn } from '../../lib/utils';

interface DoughnutChartData {
  labels: string[];
  datasets: {
    data: number[];
    backgroundColor: string[];
    borderColor?: string[];
    borderWidth?: number;
  }[];
}

interface DoughnutChartProps {
  data: DoughnutChartData;
  title: string;
  className?: string;
  /**
   * When true, always render the donut canvas even if there is only a single
   * non-zero category. Default behaviour replaces a 100% donut with a clean
   * stat block (more readable on dashboards), but pages that want chart
   * consistency across siblings should opt in.
   */
  alwaysShowChart?: boolean;
}

export const DoughnutChart: React.FC<DoughnutChartProps> = ({ data, title, className, alwaysShowChart = false }) => {
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstance = useRef<Chart | null>(null);

  const series = data?.datasets?.[0]?.data ?? [];
  const total = series.reduce((sum, v) => sum + (Number(v) || 0), 0);
  const nonZeroCount = series.filter((v) => Number(v) > 0).length;
  // Single-category mode: rendering a full-circle donut for one bucket adds
  // no information and looks oversized — show a clean stat block instead.
  // Disabled when the caller wants visual consistency across multiple charts.
  const singleCategoryMode = !alwaysShowChart && nonZeroCount === 1 && series.length === 1;

  const createChart = () => {
    if (singleCategoryMode) return;
    if (chartRef.current && data) {
      const ctx = chartRef.current.getContext('2d');

      if (ctx) {
        // Destroy previous chart if it exists
        if (chartInstance.current) {
          chartInstance.current.destroy();
        }

        // Get current theme colors
        const isDark = document.documentElement.classList.contains('dark');
        const textColor = isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)';

        // Create new chart
        chartInstance.current = new Chart(ctx, {
          type: 'doughnut',
          data: data,
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                position: 'bottom' as const,
                labels: {
                  usePointStyle: true,
                  padding: 20,
                  color: textColor,
                  generateLabels: (chart) => {
                    const items = Chart.defaults.plugins.legend.labels.generateLabels(chart);
                    const ds = chart.data.datasets?.[0]?.data as number[] | undefined;
                    const labels = (chart.data.labels ?? []) as Array<string | undefined | null>;
                    const sum = (ds ?? []).reduce((s, v) => s + (Number(v) || 0), 0);
                    if (!ds || sum <= 0) return items;
                    return items.map((it, i) => {
                      // Defensive: Chart.js sometimes hands us legend items
                      // with no text (transient state during a data update).
                      // Fall back to the underlying label, then to a dash, so
                      // we never render the literal string "undefined".
                      const baseText =
                        (typeof it.text === 'string' && it.text.length > 0 && it.text)
                        || (typeof labels[i] === 'string' && (labels[i] as string).length > 0 && (labels[i] as string))
                        || '—';
                      return {
                        ...it,
                        text: `${baseText} — ${((Number(ds[i]) || 0) / sum * 100).toFixed(1)}%`,
                      };
                    });
                  },
                },
              },
              tooltip: {
                callbacks: {
                  label: (ctx) => {
                    const v = Number(ctx.parsed) || 0;
                    const sum = (ctx.dataset.data as number[]).reduce(
                      (s, x) => s + (Number(x) || 0),
                      0
                    );
                    const pct = sum > 0 ? (v / sum * 100).toFixed(1) : '0.0';
                    return `${ctx.label}: ${v} (${pct}%)`;
                  },
                },
              },
            },
            cutout: '60%',
          },
        });
      }
    }
  };

  useEffect(() => {
    createChart();

    // Listen for theme changes
    const observer = new MutationObserver(() => {
      createChart();
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });

    return () => {
      observer.disconnect();
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [data]);

  return (
    <div className={cn("bg-card border border-border rounded-xl p-6", className)}>
      <h3 className="text-lg font-semibold text-foreground mb-4">{title}</h3>
      {singleCategoryMode ? (
        <div className="flex items-center gap-4 py-4">
          <span
            className="h-3 w-3 rounded-full flex-shrink-0"
            style={{ backgroundColor: data.datasets[0].backgroundColor[0] }}
          />
          <div className="min-w-0">
            <p className="text-3xl font-bold text-foreground tabular-nums">{total}</p>
            <p className="text-sm text-muted-foreground truncate">
              {data.labels[0]} <span className="opacity-70">— 100%</span>
            </p>
          </div>
        </div>
      ) : (
        <div className="h-64">
          <canvas ref={chartRef} />
        </div>
      )}
    </div>
  );
};
